import { getLocale } from '@/lib/paraglide/runtime'

// VND thực tế không có subunit, nhưng ERP có kịch bản bán theo kg với đơn giá
// lẻ (vd 2222,123 đ/kg) nên hiển thị giữ tối đa 3 chữ số thập phân. Pin locale
// vi-VN để tránh Windows en-US leak dấu phẩy.
const vndFormatter = new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 3,
})

/**
 * Mã hiển thị của phiếu không có mã riêng (phiếu thu, khoản chi) = 8 ký tự CUỐI
 * của id. id là UUIDv7 nên 8 ký tự đầu là mốc thời gian, trùng giữa các phiếu
 * lập gần nhau; cùng quy tắc với Báo cáo thu chi (server `docCodeSQL`).
 */
export function formatDocCode(id: string) {
    return id.length > 8 ? id.slice(-8) : id
}

export function formatVND(n: number) {
    return vndFormatter.format(n)
}

/** Format with ₫ currency symbol appended */
export function formatVnd(n: number) {
    return vndFormatter.format(n) + ' ₫'
}

// Số lượng tồn / SL bán: hiển thị có dấu phân cách hàng nghìn (vi-VN dùng "."),
// tối đa 3 chữ số thập phân để cover các đơn vị kg lẻ. Cố định locale 'vi-VN'
// tránh leak format của OS (Windows en-US sẽ ra dấu phẩy).
const qtyFormatter = new Intl.NumberFormat('vi-VN', {
    maximumFractionDigits: 3,
})

export function formatQty(n: number) {
    return qtyFormatter.format(n)
}

/** Format datetime with full date + time (day/month/year hour:minute) */
export function formatDateTime(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString(getLocale(), {
        // Thiếu timeZone thì hàm đọc theo đồng hồ MÁY NGƯỜI DÙNG. Máy ở Việt
        // Nam thì trùng nên không ai thấy, nhưng một mốc 01:30 giờ VN sẽ hiện
        // sang ngày hôm trước trên bất kỳ máy nào lệch múi giờ.
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Format datetime compact — day/month hour:minute (no year, for list cards) */
export function formatDateTimeShort(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString(getLocale(), {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Format datetime kiểu vi-VN default (HH:mm:ss d/M/yyyy) + FORCE timezone
 *  Asia/Ho_Chi_Minh. Server lưu UTC, nghiệp vụ VN — helper này đảm bảo
 *  hiển thị đúng giờ +7 bất kể browser ở múi nào (cron headless, dev nước
 *  ngoài). Output format khớp với stock-in.tsx (đã chuẩn) — dùng helper này
 *  ở mọi chỗ hiện thời gian audit / lịch sử để đồng bộ. */
export function formatDateTimeVN(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Format date-only vi-VN (d/M/yyyy) timezone Asia/Ho_Chi_Minh. Dùng cho
 *  field thuần DATE (purchase_date, processed_at) — không có giờ. */
export function formatDateVN(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Helper THỐNG NHẤT cho mọi field thời gian từ server (FE ↔ BE contract).
 *
 *  Contract:
 *  - Server (Go) trả time qua JSON theo RFC3339 (`2026-06-21T10:30:00Z`) cho
 *    cột TIMESTAMPTZ và ISO 8601 date-only (`2026-06-21`) cho cột DATE.
 *  - FE chỉ dùng `formatVN()` cho mọi field thời gian — tự detect.
 *  - Cả 2 trường hợp đều ép timezone Asia/Ho_Chi_Minh để khớp nghiệp vụ VN
 *    bất kể browser ở múi nào.
 *
 *  Quy tắc detect: input khớp pattern `YYYY-MM-DD` đứng riêng → date-only,
 *  ngược lại (có 'T' hoặc 'Z') → datetime.
 */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

export function formatVN(value: string): string {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    const opts: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Ho_Chi_Minh' }
    if (DATE_ONLY_RE.test(value)) {
        return d.toLocaleDateString('vi-VN', opts)
    }
    return d.toLocaleString('vi-VN', opts)
}

/** Ngày dd/MM/yyyy có số 0 đứng trước (`09/09/2026`, không phải `9/9/2026`).
 *  `formatDateVN`/`toLocaleDateString('vi-VN')` không tự zero-pad; dùng hàm
 *  này ở nơi cần đúng độ rộng cố định hai chữ số cho ngày/tháng. */
export function formatDatePaddedVN(value: string): string {
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleDateString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** Khoản chi: hiện GIỜ khi biết mốc thời gian thật (`spentAt`), chỉ hiện
 *  NGÀY khi không biết (khoản chi nhập tay cũ, hoặc form để trống ô giờ).
 *  KHÔNG tự vẽ "00:00" cho spentAt rỗng — đó là giờ giả, không phải "chưa
 *  biết giờ". Dạng: `dd/mm/yyyy HH:mm` (có spentAt) hoặc `dd/mm/yyyy`
 *  (không có), cả hai đều ép timezone Asia/Ho_Chi_Minh. */
export function formatExpenseDateTimeVN(
    expenseDate: string,
    spentAt?: string | null,
): string {
    if (!spentAt) return formatDatePaddedVN(expenseDate)
    const d = new Date(spentAt)
    if (isNaN(d.getTime())) return formatDatePaddedVN(expenseDate)
    const datePart = d.toLocaleDateString('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
    })
    const timePart = d.toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
        hour: '2-digit',
        minute: '2-digit',
    })
    return `${datePart} ${timePart}`
}

/** Ngày theo lịch Việt Nam dạng `YYYY-MM-DD`.
 *
 *  Dùng khi cần một KHOÁ ngày, không phải nhãn hiển thị: gom nhóm theo ngày,
 *  so sánh, hay đưa vào tham số lọc. `formatDateVN` trả `d/M/yyyy` nên không
 *  dùng làm khoá được.
 *
 *  KHÔNG dùng `value.slice(0, 10)`: chuỗi từ server là RFC3339 theo UTC, nên
 *  cắt mười ký tự đầu cho ra ngày theo UTC. Với mọi mốc từ 00:00 đến 06:59 giờ
 *  Việt Nam, ngày đó lùi một ngày. */
export function ymdVN(value: string): string {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    // en-CA cho ra sẵn dạng YYYY-MM-DD, khỏi phải ghép tay.
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' })
}

/** SĐT để hiển thị. Khách lẻ (POS, KiotViet) mang số giả toàn chữ số 0 chỉ
 *  để qua validate; hiện số đó ra chỉ gây nhiễu, nên trả chuỗi rỗng. */
export function displayPhone(phone: string | null | undefined): string {
    if (!phone || /^0+$/.test(phone.trim())) return ''
    return phone
}

/** Dung lượng tệp dạng ngắn: "1.5 MB" từ 1 MB trở lên, còn lại "12 KB" (tối thiểu 1 KB). */
export function formatBytes(n: number): string {
    if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MB`
    return `${Math.max(1, Math.round(n / 1024))} KB`
}
